/**
 * StyleSlot — the labelled style tile that sits beside References in a node's
 * Config tab (image/character/location/object/storyboard via ConfigTabContent,
 * video via VideoConfigTabContent) and in the Refine tab (RefineTabContent).
 *
 * Config uses the labelled thumbnail tile; Refine opts into a compact footer
 * pill that shares the same resolved style label and library callback.
 *
 * Shows the selected style's thumbnail and behaves like a reference thumbnail:
 * hover magnifies it (same floating preview as ReferenceThumbnailButton), a
 * single click opens the style library (owned by the caller) — this tile is
 * the only style picker, there is no control-bar pill — and a double click
 * opens the full-size style image in StyleLightbox. Same 45px tile as
 * AddReferenceButton so the row reads as one set of slots.
 */

import { memo, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Ban, Paintbrush } from 'lucide-react'
import { getStyleImageUrl } from '../../../../api/styles'
import { NO_STYLE_ID } from '../../../../constants/styles'
import { useStyleLabel } from '../../../../hooks/useStyleLabel'
import { createNadDebug } from '../../../../utils/debugLog'
import { referencePreviewSize } from './referencePreviewSize'
import { StyleLightbox } from './StyleLightbox'

const styleSlotLog = createNadDebug('dw:style-slot')

/** Same single- vs double-click window as ReferenceThumbnailButton. */
const DOUBLE_CLICK_WINDOW_MS = 280

export interface StyleSlotProps {
  currentStyleId?: string
  onOpen: () => void
  /** Style is driven by a connected upstream node, so it can't be changed here. */
  connected?: boolean
  /** Config uses a thumbnail tile; Refine uses the compact Figma footer pill. */
  presentation?: 'tile' | 'compact'
  testId?: string
}

export const StyleSlot = memo(function StyleSlot(props: StyleSlotProps) {
  const { label, thumbnailUrl } = useStyleLabel(props.currentStyleId)
  return <StyleSlotView {...props} styleLabel={label} thumbnailUrl={thumbnailUrl} />
})

StyleSlot.displayName = 'StyleSlot'

export interface StyleSlotViewProps extends StyleSlotProps {
  /** Resolved display name (see useStyleLabel). */
  styleLabel: string
  /** Raw style thumbnail from the styles API, or null. */
  thumbnailUrl: string | null
}

/**
 * Presentational half of StyleSlot — no data hooks, so tests render it
 * directly instead of relying on module mocks (order-dependent under the
 * suite's isolate:false).
 */
export const StyleSlotView = memo(function StyleSlotView({
  currentStyleId,
  onOpen,
  connected = false,
  presentation = 'tile',
  testId = 'style-slot',
  styleLabel,
  thumbnailUrl,
}: StyleSlotViewProps) {
  const { t } = useTranslation('canvas')
  // The wrapper anchors the hover preview's position (see previewPosition).
  const tileRef = useRef<HTMLDivElement>(null)
  const [hovering, setHovering] = useState(false)
  const [previewSize, setPreviewSize] = useState({ width: 200, height: 200 })
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const clickTimer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(clickTimer.current), [])
  // `NO_STYLE` is an explicit choice, so it counts as "has a style" for the
  // slot's chrome (solid border, not the dashed empty affordance).
  const hasStyle = !!currentStyleId?.trim()
  const baseLabel = hasStyle
    ? t('configTabContent.styleSelected', { name: styleLabel })
    : t('configTabContent.chooseStyle')
  const connectedHint = t('configTabContent.styleConnected')
  const imageUrl = thumbnailUrl ? getStyleImageUrl(thumbnailUrl) : null
  const interactionHint = imageUrl
    ? t('configTabContent.styleSlotHint')
    : t('configTabContent.chooseStyle')

  const openLibrary = () => {
    // A connected style is driven upstream; the tile can still enlarge it.
    if (connected) return
    styleSlotLog({ event: 'style:open-library', style_id: currentStyleId ?? null })
    onOpen()
  }

  // Same placement as ReferenceThumbnailButton: centred above the tile,
  // clamped to the viewport.
  const rect = hovering ? tileRef.current?.getBoundingClientRect() : undefined
  const previewPosition = rect
    ? {
        left: Math.max(
          8,
          Math.min(
            rect.left + rect.width / 2 - previewSize.width / 2,
            window.innerWidth - previewSize.width - 8,
          ),
        ),
        top: Math.max(
          8,
          Math.min(
            rect.top - previewSize.height - 8,
            window.innerHeight - previewSize.height - 8,
          ),
        ),
      }
    : null

  if (presentation === 'compact') {
    return (
      <button
        type="button"
        disabled={connected}
        onClick={openLibrary}
        aria-label={baseLabel}
        title={connected ? connectedHint : baseLabel}
        data-style-id={currentStyleId ?? ''}
        data-testid={testId}
        className="nodrag flex shrink-0 items-center gap-1.5 rounded-full border border-input-border bg-surface-1 px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default disabled:opacity-60"
      >
        <Paintbrush className="h-3 w-3" aria-hidden="true" />
        {t('configTabContent.style')}
      </button>
    )
  }

  return (
    <div>
      <label className="text-xxs uppercase tracking-wider text-text-muted block mb-1">
        {t('configTabContent.style')}
      </label>
      <div className="flex items-center min-h-[36px]" data-testid="style-row">
        <div
          ref={tileRef}
          className="flex-shrink-0"
          onMouseEnter={() => {
            if (!imageUrl) return
            setHovering(true)
            styleSlotLog({
              event: 'style:hover-preview',
              style_id: currentStyleId ?? null,
              connected,
            })
          }}
          onMouseLeave={() => setHovering(false)}
        >
          <button
            type="button"
            onClick={(event) => {
              setHovering(false)
              // Keyboard activation (Enter/Space) has no double-click to wait for.
              if (event.detail === 0) {
                openLibrary()
                return
              }
              clearTimeout(clickTimer.current)
              clickTimer.current = setTimeout(openLibrary, imageUrl ? DOUBLE_CLICK_WINDOW_MS : 0)
            }}
            onDoubleClick={(event) => {
              clearTimeout(clickTimer.current)
              event.preventDefault()
              if (!imageUrl) return
              setHovering(false)
              styleSlotLog({ event: 'style:open-lightbox', style_id: currentStyleId ?? null, connected })
              setLightboxOpen(true)
            }}
            onBlur={() => setHovering(false)}
            // aria-disabled, not disabled: a connected style can't be changed
            // here but double-click must still enlarge it, and a disabled
            // button swallows every click.
            aria-disabled={connected ? 'true' : undefined}
            title={connected ? connectedHint : interactionHint}
            aria-label={connected ? `${baseLabel} · ${connectedHint}` : baseLabel}
            data-connected={connected ? 'true' : undefined}
            className={`nodrag flex items-center justify-center overflow-hidden rounded-lg border border-input-border bg-surface-1 text-text-muted hover:bg-surface-3 hover:border-input-border-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary transition-colors aria-disabled:opacity-60 ${
              hasStyle ? '' : 'border-dashed'
            }`}
            style={{ width: 45, height: 45 }}
            data-testid={testId}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : currentStyleId === NO_STYLE_ID ? (
              // Mirrors the "no style" tile in StyleLibraryModal.
              <Ban aria-hidden="true" className="w-4 h-4" strokeWidth={1.5} />
            ) : hasStyle ? (
              // A style is set but carries no thumbnail (e.g. resolved from
              // the static VISUAL_STYLES fallback while the API loads).
              <span className="text-sm font-bold text-foreground/40">
                {styleLabel.charAt(0)}
              </span>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            )}
          </button>
        </div>
      </div>
      {hovering &&
        imageUrl &&
        previewPosition &&
        createPortal(
          <div
            data-testid="style-hover-preview"
            className="fixed z-[9998] pointer-events-none overflow-hidden rounded-lg border border-border bg-surface-0 shadow-xl"
            style={{ ...previewPosition, ...previewSize }}
          >
            <img
              src={imageUrl}
              alt={styleLabel}
              className="h-full w-full object-contain"
              onLoad={(event) => {
                setPreviewSize(
                  referencePreviewSize(
                    event.currentTarget.naturalWidth,
                    event.currentTarget.naturalHeight,
                  ),
                )
              }}
            />
          </div>,
          document.body,
        )}
      <StyleLightbox
        imageUrl={lightboxOpen ? imageUrl : null}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  )
})

StyleSlotView.displayName = 'StyleSlotView'
